import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CampaignTimelineStage,
  CampaignTimelineStageId,
  DeliverableTimelineMap,
  DragMode,
  DragState,
  GanttCreator,
} from "../types";
import { applyDragToStages } from "../utils/stageUtils";

interface UseGanttDragProps {
  editMode: boolean;
  creators: GanttCreator[];
  selectedCreatorIds: Set<string | number>;
  setSelectedCreatorIds: React.Dispatch<React.SetStateAction<Set<string | number>>>;
  timelineByDeliverable?: DeliverableTimelineMap;
  rangeStart: Date;
  rangeEnd: Date;
  totalDays: number;
  onUpdateDeliverableTimeline?: (
    creatorId: string | number,
    deliverableId: string,
    next: CampaignTimelineStage[]
  ) => void;
  onChangeDeliverableTimeline?: (
    deliverableKey: string,
    next: CampaignTimelineStage[]
  ) => void;
  setUserAdjusted: (val: boolean) => void;
}

export function useGanttDrag({
  editMode,
  creators,
  selectedCreatorIds,
  setSelectedCreatorIds,
  timelineByDeliverable,
  rangeStart,
  rangeEnd,
  totalDays,
  onUpdateDeliverableTimeline,
  onChangeDeliverableTimeline,
  setUserAdjusted,
}: UseGanttDragProps) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const trackRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const dragRef = useRef(drag);
  dragRef.current = drag;

  const pxToDays = useCallback(
    (px: number, trackKey: string) => {
      const el = trackRefs.current.get(trackKey);
      const width = el?.clientWidth || 1;
      return Math.round((px / width) * totalDays);
    },
    [totalDays]
  );

  const previewMap = useMemo((): DeliverableTimelineMap | undefined => {
    if (!drag || !drag.dayDelta) return timelineByDeliverable;

    const next: DeliverableTimelineMap = { ...(timelineByDeliverable || {}) };
    const isMultiCreator = drag.selectedCreatorIds.length > 1;

    if (!isMultiCreator) {
      const deliverableKey = `${drag.creatorId}_${drag.deliverableId}`;
      const current =
        timelineByDeliverable?.[deliverableKey] ||
        timelineByDeliverable?.[drag.deliverableId] ||
        timelineByDeliverable?.[drag.deliverableName] ||
        [];

      const updated = applyDragToStages(
        current,
        drag.mode,
        drag.stageId,
        drag.dayDelta,
        rangeStart,
        rangeEnd
      );
      next[deliverableKey] = updated;
      next[drag.deliverableId] = updated;
      return next;
    }

    const targetCreators = creators.filter((c) =>
      drag.selectedCreatorIds.includes(c.id)
    );

    targetCreators.forEach((creator) => {
      creator.deliverables.forEach((del) => {
        const deliverableKey = `${creator.id}_${del.id}`;
        const current =
          timelineByDeliverable?.[deliverableKey] ||
          timelineByDeliverable?.[del.id] ||
          del.stages ||
          timelineByDeliverable?.[del.name] ||
          [];

        const updated = applyDragToStages(
          current,
          drag.mode,
          drag.stageId,
          drag.dayDelta,
          rangeStart,
          rangeEnd
        );
        next[deliverableKey] = updated;
        next[del.id] = updated;
      });
    });

    return next;
  }, [creators, drag, rangeEnd, rangeStart, timelineByDeliverable]);

  const commitDrag = useCallback(
    (state: DragState) => {
      if (!state.dayDelta) {
        setDrag(null);
        return;
      }

      const isMultiCreator = state.selectedCreatorIds.length > 1;

      if (!isMultiCreator) {
        const deliverableKey = `${state.creatorId}_${state.deliverableId}`;
        const current =
          timelineByDeliverable?.[deliverableKey] ||
          timelineByDeliverable?.[state.deliverableId] ||
          timelineByDeliverable?.[state.deliverableName] ||
          [];

        const next = applyDragToStages(
          current,
          state.mode,
          state.stageId,
          state.dayDelta,
          rangeStart,
          rangeEnd
        );

        onUpdateDeliverableTimeline?.(state.creatorId, state.deliverableId, next);
        onChangeDeliverableTimeline?.(deliverableKey, next);
      } else {
        const targetCreators = creators.filter((c) =>
          state.selectedCreatorIds.includes(c.id)
        );

        targetCreators.forEach((creator) => {
          creator.deliverables.forEach((del) => {
            const deliverableKey = `${creator.id}_${del.id}`;
            const current =
              timelineByDeliverable?.[deliverableKey] ||
              timelineByDeliverable?.[del.id] ||
              del.stages ||
              timelineByDeliverable?.[del.name] ||
              [];

            const next = applyDragToStages(
              current,
              state.mode,
              state.stageId,
              state.dayDelta,
              rangeStart,
              rangeEnd
            );

            onUpdateDeliverableTimeline?.(creator.id, del.id, next);
            onChangeDeliverableTimeline?.(deliverableKey, next);
          });
        });
      }

      setUserAdjusted(true);
      setDrag(null);
    },
    [
      creators,
      onChangeDeliverableTimeline,
      onUpdateDeliverableTimeline,
      rangeEnd,
      rangeStart,
      setUserAdjusted,
      timelineByDeliverable,
    ]
  );

  useEffect(() => {
    if (!drag) return;

    const onMove = (e: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      const trackKey = `${current.creatorId}-${current.deliverableId}`;
      const dayDelta = pxToDays(e.clientX - current.originX, trackKey);
      setDrag((prev) => (prev ? { ...prev, dayDelta } : prev));
    };

    const onUp = () => {
      const current = dragRef.current;
      dragRef.current = null;
      if (current) {
        commitDrag(current);
      } else {
        setDrag(null);
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [commitDrag, drag, pxToDays]);

  const beginDrag = (
    e: React.PointerEvent,
    mode: DragMode,
    row: {
      creatorId: string | number;
      deliverableName: string;
      deliverableId: string;
    },
    stageId: CampaignTimelineStageId
  ) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();

    const hasOtherChecked =
      selectedCreatorIds.size > 1 ||
      (selectedCreatorIds.size === 1 && !selectedCreatorIds.has(row.creatorId));

    const targetIds = hasOtherChecked
      ? Array.from(new Set([...Array.from(selectedCreatorIds), row.creatorId]))
      : [row.creatorId];

    setDrag({
      mode,
      creatorId: row.creatorId,
      deliverableName: row.deliverableName,
      deliverableId: row.deliverableId,
      stageId,
      selectedCreatorIds: targetIds,
      originX: e.clientX,
      dayDelta: 0,
    });
  };

  const registerTrackRef = useCallback((key: string, el: HTMLDivElement | null) => {
    if (el) {
      trackRefs.current.set(key, el);
    } else {
      trackRefs.current.delete(key);
    }
  }, []);

  return {
    drag,
    previewMap,
    beginDrag,
    registerTrackRef,
  };
}
